import moment from 'moment';
import formatCurrency from 'format-currency';
import LaborExpenseCostBreakdownModalConfig from './modals/labor-expense-cost-breakdown-modal/labor-expense-cost-breakdown-modal.component';
import LaborProjectCostBreakdownModalConfig from './modals/labor-project-cost-breakdown-modal/labor-project-cost-breakdown-modal.component';

const LABOR_PIE_PALETTE = [
  '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c',
  '#34495e', '#e67e22', '#95a5a6', '#16a085', '#c0392b', '#2980b9',
  '#8e44ad', '#27ae60', '#d35400', '#bdc3c7'
];

/**
 * Labor distribution ledger: GET /api/reports/labor-distribution/ledger
 * Response: { page: SpringPage, statistics } (not a bare Page). Table uses page.content.
 * Sort/filter id keys in URLs: eid, ltid, cid, ctid, pid, lcid (see column.sortValue / filterIdField).
 */
class LaborDistributionReportController {
  constructor($rootScope, $http, toastr, $uibModal) {
    "ngInject";
    const self = this;
    self.$rootScope = $rootScope;
    self.$http = $http;
    self.toastr = toastr;
    self.$uibModal = $uibModal;

    /** Range-wide aggregates from wrapped ledger GET (see response.statistics). */
    self.laborLedgerDefaultSort = 'eid,asc';
    self.laborStatistics = null;
    /** Toggled true to reload spring-data-table after cache invalidate. */
    self.laborTableFilterRefresh = false;
    /** Prevent double-clicks during PDF blob download */
    self.laborLedgerExportingPdf = false;

    const ytd = {
      from: moment().clone().startOf('year').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD')
    };

    self.laborFilter = {
      startDate: ytd.from,
      endDate: ytd.to,
      customer: '0',
      contract: '0',
      project: '0',
      billable: 'all',
      expenseAccountName: '0',
      laborCategory: '0',
      chargeCode: '0',
      employee: '0',
      timesheetPeriod: '0'
    };

    /** String dimensions sampled for multi-select filters; customer/contract/project/laborCategory/employee send ids (see column filterIdField). */
    self._distStringFields = [
      'customer',
      'contract',
      'project',
      'expenseAccountName',
      'laborCategory',
      'chargeCode',
      'employee',
      'timesheetPeriod'
    ];

    self.formatDateToLocalDate = function(dateValue) {
      if (!dateValue) return null;
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      if (dateValue instanceof Date) {
        const y = dateValue.getFullYear();
        const m = String(dateValue.getMonth() + 1).padStart(2, '0');
        const d = String(dateValue.getDate()).padStart(2, '0');
        return y + '-' + m + '-' + d;
      }
      if (typeof dateValue === 'string') {
        const parsed = new Date(dateValue);
        if (!isNaN(parsed.getTime())) {
          const y = parsed.getFullYear();
          const m = String(parsed.getMonth() + 1).padStart(2, '0');
          const d = String(parsed.getDate()).padStart(2, '0');
          return y + '-' + m + '-' + d;
        }
      }
      return null;
    };

    self._defaultReportingDates = function() {
      const now = moment();
      return {
        from: now.clone().startOf('year').format('YYYY-MM-DD'),
        to: now.format('YYYY-MM-DD')
      };
    };

    self._appendParam = function(parts, key, raw) {
      if (!raw || raw === '0' || raw === '-1') return;
      parts.push(key + '=' + encodeURIComponent(raw));
    };

    self.laborLedgerFilter = function() {
      let start = self.formatDateToLocalDate(self.laborFilter.startDate);
      let end = self.formatDateToLocalDate(self.laborFilter.endDate);
      const defs = self._defaultReportingDates();
      if (!start) start = defs.from;
      if (!end) end = defs.to;

      var parts = [
        'startDate=' + encodeURIComponent(start),
        'endDate=' + encodeURIComponent(end)
      ];

      if (self.laborFilter.billable === 'direct') {
        parts.push('billable=' + encodeURIComponent('Direct Labor'));
      } else if (self.laborFilter.billable === 'indirect') {
        parts.push('billable=' + encodeURIComponent('Indirect Labor'));
      } else if (self.laborFilter.billable === 'none') {
        parts.push('billable=none');
      }

      self._distStringFields.forEach(function(field) {
        self._appendParam(parts, field, self.laborFilter[field]);
      });

      return parts.join('&');
    };

    self.reportingPeriodPreset = 'thisYear';
    self.customStartDate = moment(ytd.from, 'YYYY-MM-DD').toDate();
    self.customEndDate = moment(ytd.to, 'YYYY-MM-DD').toDate();
    self.opened = {};
    self.open = function($event, key) {
      $event.preventDefault();
      $event.stopPropagation();
      self.opened[key] = !self.opened[key];
    };
    self.datePickerFormat = 'MMM dd, yyyy';
    self.datePickerOptions = {
      formatYear: 'yyyy',
      formatMonth: 'MMM',
      formatDay: 'dd',
      minDate: new Date(1999, 0, 1),
      startingDay: 1
    };

    /** Preset/custom dates → laborFilter + table refresh + distinct options. */
    self.applyLaborDateRangeFromControls = function() {
      var mNow = moment();
      var startM;
      var endM;

      if (self.reportingPeriodPreset === 'custom') {
        if (!self.customStartDate) {
          self.customStartDate = mNow.clone().startOf('month').toDate();
        }
        if (!self.customEndDate) {
          self.customEndDate = mNow.clone().toDate();
        }
        startM = moment(self.customStartDate).startOf('day');
        endM = moment(self.customEndDate).startOf('day');
      } else {
        switch (self.reportingPeriodPreset) {
          case 'thisMonth':
            startM = mNow.clone().startOf('month');
            endM = mNow.clone();
            break;
          case 'lastMonth':
            startM = mNow.clone().subtract(1, 'month').startOf('month');
            endM = mNow.clone().subtract(1, 'month').endOf('month');
            break;
          case 'lastYear':
            startM = mNow.clone().subtract(1, 'year').startOf('year');
            endM = mNow.clone().subtract(1, 'year').endOf('year');
            break;
          case 'thisYear':
          default:
            startM = mNow.clone().startOf('year');
            endM = mNow.clone();
            break;
        }
      }

      if (endM.isBefore(startM)) {
        endM = startM.clone();
        if (self.reportingPeriodPreset === 'custom') {
          self.customEndDate = endM.toDate();
        }
      }

      self.laborFilter.startDate = startM.format('YYYY-MM-DD');
      self.laborFilter.endDate = endM.format('YYYY-MM-DD');
      self.customStartDate = startM.toDate();
      self.customEndDate = endM.toDate();
      self._bootstrapDistinctStringFilters();
      self.laborTableFilterRefresh = true;
    };

    self.onReportingPeriodPresetChange = function() {
      if (self.reportingPeriodPreset === 'custom') {
        self.customStartDate = moment(self.laborFilter.startDate, 'YYYY-MM-DD').toDate();
        self.customEndDate = moment(self.laborFilter.endDate, 'YYYY-MM-DD').toDate();
      }
      self.applyLaborDateRangeFromControls();
    };

    self._applyMultiCsvFilter = function(column, fieldKey) {
      var selectedVals = column.filterOptions.filter(function(opt) {
        return opt.selected && opt.id !== 0 && opt.id !== 'all';
      }).map(function(opt) {
        return String(opt.id);
      });
      var allOpt = column.filterOptions.find(function(opt) {
        return opt.id === 0 || opt.id === 'all';
      });
      var totalChoices = column.filterOptions.filter(function(opt) {
        return opt.id !== 0 && opt.id !== 'all';
      }).length;

      if (allOpt && allOpt.selected) {
        column.filterOptions.forEach(function(opt) {
          if (opt.id !== 'all') opt.selected = true;
        });
        self.laborFilter[fieldKey] = '0';
        return;
      }
      if (selectedVals.length === 0) {
        if (allOpt) allOpt.selected = false;
        self.laborFilter[fieldKey] = '-1';
        return;
      }
      if (totalChoices > 0 && selectedVals.length === totalChoices) {
        if (allOpt) allOpt.selected = true;
        self.laborFilter[fieldKey] = '0';
        return;
      }
      if (allOpt) allOpt.selected = false;
      self.laborFilter[fieldKey] = selectedVals.join(',');
    };

    self._ledgerRowsFromResponse = function(data) {
      if (!data) {
        return [];
      }
      if (data.page && data.page.content) {
        return data.page.content;
      }
      return data.content || [];
    };

    self._bootstrapDistinctStringFilters = function() {
      var q = self.laborLedgerFilter();
      var url = self.$rootScope.apiHost +
        '/api/reports/labor-distribution/ledger?page=0&size=750&sort=eid,asc' +
        (q ? '&' + q : '');

      self.$http.get(url, { withCredentials: true }).then(function(res) {
        var rows = self._ledgerRowsFromResponse(res.data);
        self._distStringFields.forEach(function(field) {
          var column = self.laborDistributionColumns.find(function(c) {
            return c.value === field;
          });
          if (!column || !column.filterable) return;

          var uniq = {};
          rows.forEach(function(row) {
            if (column.filterIdField) {
              var fid = row[column.filterIdField];
              if (fid === null || fid === undefined || String(fid).trim() === '') {
                if (field === 'employee') {
                  fid = row.employeeId;
                } else if (field === 'customer') {
                  fid = row.customerId;
                } else if (field === 'contract') {
                  fid = row.contractId;
                } else if (field === 'project') {
                  fid = row.projectId;
                } else if (field === 'laborCategory') {
                  fid = row.laborCategoryId;
                }
              }
              if (fid === null || fid === undefined || String(fid).trim() === '') {
                return;
              }
              var idStr = String(fid).trim();
              if (!uniq[idStr]) {
                var lbl = row[field];
                uniq[idStr] = (lbl !== null && lbl !== undefined && String(lbl).trim() !== '')
                  ? String(lbl).trim()
                  : idStr;
              }
            } else {
              var v = row[field];
              if (v !== null && v !== undefined && String(v).trim() !== '') {
                var vs = String(v).trim();
                uniq[vs] = vs;
              }
            }
          });
          var keys = Object.keys(uniq).sort(function(a, b) {
            return String(uniq[a]).toLowerCase().localeCompare(String(uniq[b]).toLowerCase());
          });

          var cur = self.laborFilter[field];
          var currentSet = {};
          if (cur && cur !== '0' && cur !== '-1') {
            cur.split(',').forEach(function(x) {
              if (x) currentSet[x] = true;
            });
          }
          var allSelected = keys.length === 0 || keys.every(function(k) {
            return currentSet[k];
          }) || Object.keys(currentSet).length === 0;

          column.filterOptions = [
            { id: 0, name: 'All', label: 'All', selected: allSelected || cur === '' || cur === '0' }
          ].concat(keys.map(function(k) {
            var label = uniq[k];
            return {
              id: k,
              name: label,
              label: label,
              selected: allSelected || !!currentSet[k]
            };
          }));

          var scc = column.filterOptions.filter(function(opt) {
            return opt.id !== 0 && opt.selected;
          }).length;
          var tcc = Math.max(column.filterOptions.length - 1, 0);
          column.hasActiveFilter = tcc > 0 && scc < tcc;
          column.filteredCount = column.hasActiveFilter && scc > 0 ? scc : null;
        });
      });
    };

    self.laborDistributionColumns = [
      {
        displayName: 'Type',
        value: 'laborType',
        sortValue: 'ltid',
        sortable: true,
        dataType: 'text',
        filterable: true,
        formatting: function(record) {
          return record.laborType != null && record.laborType !== '' ? String(record.laborType) : '';
        },
        filterOptions: [
          { id: 'all', name: 'All', label: 'All', selected: true },
          { id: 'direct', name: 'Direct Labor', label: 'Direct Labor', selected: true },
          { id: 'indirect', name: 'Indirect Labor', label: 'Indirect Labor', selected: true }
        ],
        filterFunction: function(column) {
          var sel = column.filterOptions.filter(function(opt) {
            return opt.selected && opt.id !== 'all';
          });
          var allOpt = column.filterOptions.find(function(opt) {
            return opt.id === 'all';
          });
          if (allOpt && allOpt.selected) {
            column.filterOptions.forEach(function(o) {
              o.selected = true;
            });
            self.laborFilter.billable = 'all';
          } else if (sel.length === 0) {
            if (allOpt) allOpt.selected = false;
            self.laborFilter.billable = 'none';
          } else if (sel.length === column.filterOptions.length - 1) {
            if (allOpt) allOpt.selected = true;
            self.laborFilter.billable = 'all';
          } else if (sel.length === 1) {
            if (allOpt) allOpt.selected = false;
            self.laborFilter.billable = sel[0].id === 'direct' ? 'direct' : 'indirect';
          } else {
            if (allOpt) allOpt.selected = false;
            self.laborFilter.billable = 'all';
          }
        }
      },
      strCol('Expense account', 'expenseAccountName', { sortable: false }),
      strCol('Customer', 'customer', { sortValue: 'cid', filterIdField: 'cid' }),
      strCol('Contract', 'contract', { sortValue: 'ctid', filterIdField: 'ctid' }),
      strCol('Project', 'project', { sortValue: 'pid', filterIdField: 'pid' }),
      strCol('Labor category', 'laborCategory', { sortValue: 'lcid', filterIdField: 'lcid' }),
      strCol('Employee', 'employee', { sortValue: 'eid', filterIdField: 'eid' }),
      strCol('Timesheet period', 'timesheetPeriod'),
      strCol('Charge code', 'chargeCode'),
      {
        displayName: 'Hours',
        value: 'hours',
        sortable: true,
        dataType: 'number',
        formatting: function(record) {
          if (record.hours === null || record.hours === undefined) return '';
          return Number(record.hours).toFixed(2);
        }
      },
      {
        displayName: 'Cost',
        value: 'cost',
        sortable: true,
        dataType: 'number',
        formatting: function(record) {
          if (record.cost === null || record.cost === undefined) return '';
          return '$' + formatCurrency(record.cost);
        }
      }
    ];

    function strCol(label, field, opts) {
      opts = opts || {};
      var col = {
        displayName: label,
        value: field,
        sortable: opts.sortable !== false,
        dataType: 'text',
        filterable: true,
        filterOptions: [{ id: 0, name: 'All', label: 'All', selected: true }],
        filterFunction: function(column) {
          self._applyMultiCsvFilter(column, field);
        }
      };
      if (opts.sortValue) {
        col.sortValue = opts.sortValue;
      }
      if (opts.filterIdField) {
        col.filterIdField = opts.filterIdField;
      }
      return col;
    }

    self.formatStatCurrency = function(n) {
      if (n === null || n === undefined || n === '') {
        return '';
      }
      return '$' + formatCurrency(n);
    };

    self.statLaborCostForType = function(label) {
      var m = self.laborStatistics && self.laborStatistics.costByLaborType;
      if (!m || typeof m !== 'object') {
        return 0;
      }
      var v = m[label];
      return typeof v === 'number' ? v : 0;
    };

    /** Chart.js doughnuts (angular-chart); legend off — cost breakdown modal. */
    self.laborExpensePie = { labels: [], data: [], colors: [] };
    self.laborProjectPie = { labels: [], data: [], colors: [] };
    /** Stable refs for ng-repeat — never rebuild in template getters (avoids $digest loops). */
    self.laborExpenseBreakdownRows = [];
    self.laborProjectBreakdownRows = [];

    self.laborPieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutoutPercentage: 56,
      legend: {
        display: false
      },
      tooltips: {
        callbacks: {
          label: function(tooltipItem, data) {
            var label = data.labels[tooltipItem.index] || '';
            var v = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            return label + ': ' + self.formatStatCurrency(v);
          }
        }
      }
    };

    /** Human-readable ledger date range for UI (aligned with laborLedgerFilter). */
    self._formatLaborLedgerReportPeriodLabel = function() {
      var start = self.formatDateToLocalDate(self.laborFilter.startDate);
      var end = self.formatDateToLocalDate(self.laborFilter.endDate);
      var defs = self._defaultReportingDates();
      if (!start) {
        start = defs.from;
      }
      if (!end) {
        end = defs.to;
      }
      var sm = moment(start, 'YYYY-MM-DD', true);
      var em = moment(end, 'YYYY-MM-DD', true);
      if (!sm.isValid() || !em.isValid()) {
        return '';
      }
      return 'Reporting period: ' + sm.format('MMM D, YYYY') + ' – ' + em.format('MMM D, YYYY');
    };

    /** Expense-account donut + table (opened from Total cost widget). */
    self.openLaborExpenseCostBreakdownModal = function() {
      if (!self.laborStatistics) {
        return;
      }
      var modalCfg = Object.assign({}, LaborExpenseCostBreakdownModalConfig, {
        resolve: {
          reportPeriodLabel: function() {
            return self._formatLaborLedgerReportPeriodLabel();
          },
          laborExpensePie: function() {
            return self.laborExpensePie;
          },
          laborPieChartOptions: function() {
            return self.laborPieChartOptions;
          },
          laborExpenseBreakdownRows: function() {
            return self.laborExpenseBreakdownRows;
          },
          formatStatCurrencyFn: function() {
            return function(n) {
              return self.formatStatCurrency(n);
            };
          }
        }
      });
      self.$uibModal.open(modalCfg).result.catch(function() {
        /* dismissed */
      });
    };

    /** Project donut + table (opened from Direct labor widget). */
    self.openLaborProjectCostBreakdownModal = function() {
      if (!self.laborStatistics) {
        return;
      }
      var modalCfg = Object.assign({}, LaborProjectCostBreakdownModalConfig, {
        resolve: {
          reportPeriodLabel: function() {
            return self._formatLaborLedgerReportPeriodLabel();
          },
          laborProjectPie: function() {
            return self.laborProjectPie;
          },
          laborPieChartOptions: function() {
            return self.laborPieChartOptions;
          },
          laborProjectBreakdownRows: function() {
            return self.laborProjectBreakdownRows;
          },
          formatStatCurrencyFn: function() {
            return function(n) {
              return self.formatStatCurrency(n);
            };
          }
        }
      });
      self.$uibModal.open(modalCfg).result.catch(function() {
        /* dismissed */
      });
    };

    self._rowsFromPieModel = function(model) {
      if (!model || !model.labels || !model.data || model.labels.length === 0) {
        return [];
      }
      return model.labels.map(function(l, idx) {
        return { name: l, cost: model.data[idx] };
      });
    };

    self._truncateChartLabel = function(raw, maxLen) {
      var s = raw == null ? '' : String(raw).trim();
      if (s === '') {
        return '(Unnamed)';
      }
      maxLen = maxLen || 44;
      if (s.length <= maxLen) {
        return s;
      }
      return s.slice(0, maxLen - 3) + '...';
    };

    /** Display label from statistics.costByProject (API variants: projectName, name, project; else #id when id only). */
    self._statisticsProjectRowLabel = function(row) {
      if (!row || typeof row !== 'object') {
        return '';
      }
      var parts = [row.projectName, row.name, row.project];
      for (var i = 0; i < parts.length; i++) {
        if (parts[i] != null && String(parts[i]).trim() !== '') {
          return String(parts[i]).trim();
        }
      }
      var rid =
        row.projectId !== undefined && row.projectId !== null && row.projectId !== ''
          ? row.projectId
          : row.pid;
      var nid = Number(rid);
      if (!isNaN(nid) && nid > 0) {
        return '#' + nid;
      }
      return '';
    };

    var PLACEHOLDER_PROJECT_LABELS = {
      '(unnamed)': true,
      'n/a': true,
      na: true,
      none: true,
      unassigned: true,
      'no project': true,
      '(no project)': true,
      '-': true,
      '--': true,
      '\u2014': true,
      '\u2013': true
    };

    /** True when costByProject row counts as assigned to a real project (excludes orphan / placeholder buckets). */
    self._statisticsProjectSliceShouldInclude = function(row) {
      if (!row || typeof row !== 'object') {
        return false;
      }

      function nameIsPlaceholder(t) {
        var x = t.trim().toLowerCase();
        if (!x.length) {
          return true;
        }
        if (PLACEHOLDER_PROJECT_LABELS[x]) {
          return true;
        }
        if (/^no project\b/.test(x)) {
          return true;
        }
        return false;
      }

      var rawId =
        row.projectId !== undefined && row.projectId !== null && row.projectId !== ''
          ? row.projectId
          : row.pid;
      var hasExplicitId =
        (row.projectId !== undefined &&
          row.projectId !== null &&
          String(row.projectId).trim() !== '') ||
        (row.pid !== undefined &&
          row.pid !== null &&
          String(row.pid).trim() !== '');

      var idPositive = false;
      if (hasExplicitId) {
        var nid = Number(rawId);
        if (!isNaN(nid)) {
          idPositive = nid > 0;
        } else {
          idPositive = typeof rawId === 'string' && String(rawId).trim() !== '';
        }
      }

      if (hasExplicitId && !idPositive) {
        return false;
      }

      var label = self._statisticsProjectRowLabel(row);
      if (!label.length) {
        return false;
      }
      if (nameIsPlaceholder(label)) {
        return false;
      }
      return true;
    };

    /** Rows normalized for _buildLaborPieModel (drops unassigned buckets). */
    self._statisticsCostByProjectForPie = function(rows) {
      var out = [];
      (rows || []).forEach(function(r) {
        if (!self._statisticsProjectSliceShouldInclude(r)) {
          return;
        }
        var label = self._statisticsProjectRowLabel(r);
        out.push({
          projectName: label,
          totalCost: r.totalCost
        });
      });
      return out;
    };

    /**
     * @param {number} [options.maxSlices=14] - Use Infinity to show every slice (no "Other" rollup).
     */
    self._buildLaborPieModel = function(rows, labelKey, valueKey, options) {
      var maxSlices = 14;
      if (options && Object.prototype.hasOwnProperty.call(options, 'maxSlices')) {
        maxSlices = options.maxSlices;
      }
      var items = [];
      (rows || []).forEach(function(r) {
        var v = Number(r[valueKey]);
        if (!isNaN(v) && v > 0) {
          items.push({
            label: self._truncateChartLabel(r[labelKey], 44),
            value: v
          });
        }
      });
      if (options && options.excludeUnlabeledPieSlices) {
        items = items.filter(function(it) {
          var lc = String(it.label || '').trim().toLowerCase();
          return lc.length > 0 && lc !== '(unnamed)';
        });
      }

      items.sort(function(a, b) {
        return b.value - a.value;
      });

      if (Number.isFinite(maxSlices) && items.length > maxSlices) {
        var top = items.slice(0, maxSlices);
        var remainder = items.slice(maxSlices).reduce(function(sum, x) {
          return sum + x.value;
        }, 0);
        if (remainder > 0) {
          top.push({
            label: 'Other (' + (items.length - maxSlices) + ')',
            value: remainder
          });
        }
        items = top;
      }

      return {
        labels: items.map(function(x) {
          return x.label;
        }),
        data: items.map(function(x) {
          return x.value;
        }),
        colors: items.map(function(_, idx) {
          return LABOR_PIE_PALETTE[idx % LABOR_PIE_PALETTE.length];
        })
      };
    };

    self._syncLaborChartsFromStatistics = function(stat) {
      if (!stat) {
        self.laborExpensePie = { labels: [], data: [], colors: [] };
        self.laborProjectPie = { labels: [], data: [], colors: [] };
        self.laborExpenseBreakdownRows = [];
        self.laborProjectBreakdownRows = [];
        return;
      }
      self.laborExpensePie = self._buildLaborPieModel(stat.costByExpenseAccount || [], 'accountName', 'totalCost');
      var projectSlices = self._statisticsCostByProjectForPie(stat.costByProject || []);
      self.laborProjectPie = self._buildLaborPieModel(projectSlices, 'projectName', 'totalCost', {
        maxSlices: Infinity,
        excludeUnlabeledPieSlices: true
      });
      self.laborExpenseBreakdownRows = self._rowsFromPieModel(self.laborExpensePie);
      self.laborProjectBreakdownRows = self._rowsFromPieModel(self.laborProjectPie);
    };

    self.handleLaborLedgerResponse = function(response) {
      if (response && response.statistics) {
        self.laborStatistics = response.statistics;
        self._syncLaborChartsFromStatistics(response.statistics);
      } else {
        self.laborStatistics = null;
        self._syncLaborChartsFromStatistics(null);
      }
    };

    /**
     * PDF export uses the same dates and filter query string as the ledger grid (no page/size/sort).
     * GET /api/reports/labor-distribution/ledger/export/pdf
     */
    self.exportLaborLedgerPdf = function() {
      if (self.laborLedgerExportingPdf) {
        return;
      }
      var query = self.laborLedgerFilter();
      var url = self.$rootScope.apiHost +
        '/api/reports/labor-distribution/ledger/export/pdf?' + query;

      self.laborLedgerExportingPdf = true;
      self.$http
        .get(url, { responseType: 'arraybuffer', withCredentials: true })
        .then(function(res) {
          var disposition = '';
          try {
            disposition = typeof res.headers === 'function'
              ? (res.headers('content-disposition') || res.headers('Content-Disposition') || '')
              : '';
          } catch (e) {
            disposition = '';
          }
          var filename = 'Labor_Distribution_Report.pdf';
          var match = /filename\*?=(?:UTF-8''|)([^";]+)|filename="?([^";]+)"?/i.exec(disposition || '');
          if (match && (match[1] || match[2])) {
            filename = decodeURIComponent((match[1] || match[2]).trim());
          }
          var mime = res.headers ? (res.headers('content-type') || 'application/pdf') : 'application/pdf';
          var blob = new Blob([res.data], { type: mime });
          var blobUrl = window.URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(function(errRes) {
          var msg = 'Could not download labor distribution PDF.';
          if (errRes && errRes.data) {
            var enc = errRes.headers && typeof errRes.headers === 'function'
              ? errRes.headers('content-type')
              : '';
            var uint8View = errRes.data && errRes.data.byteLength != null ? new Uint8Array(errRes.data) : null;
            if (
              uint8View &&
              errRes.headers &&
              String(enc || '').indexOf('json') !== -1 &&
              typeof TextDecoder !== 'undefined'
            ) {
              try {
                var decoded = JSON.parse(new TextDecoder('utf-8').decode(uint8View));
                if (decoded && (decoded.message || decoded.error || decoded.detail)) {
                  msg = decoded.message || decoded.detail || decoded.error || msg;
                }
              } catch (_e) {}
            }
          }
          self.toastr.error(msg, 'Export failed');
        })
        .finally(function() {
          self.laborLedgerExportingPdf = false;
        });
    };

    /** @param limitToRange when true send startDate/endDate (clears slice only); omit = clear entire cache server-side */
    self.clearLaborLedgerCache = function(limitToRange) {
      var url = self.$rootScope.apiHost + '/api/reports/labor-distribution/cache';
      var cfg = { withCredentials: true };
      if (limitToRange) {
        cfg.params = {};
        cfg.params.startDate = self.formatDateToLocalDate(self.laborFilter.startDate) || self._defaultReportingDates().from;
        cfg.params.endDate = self.formatDateToLocalDate(self.laborFilter.endDate) || self._defaultReportingDates().to;
      }
      self.$http.delete(url, cfg).then(function() {
        self.toastr.success('Labor distribution ledger cache cleared.');
        self.laborTableFilterRefresh = true;
      }, function() {
        self.toastr.error('Could not clear labor distribution ledger cache.');
      });
    };

    self._bootstrapDistinctStringFilters();
  }
}

export default LaborDistributionReportController;



// WEBPACK FOOTER //
// ./client/pages/admin/reports/canned-reports/labor-distribution-report/labor-distribution-report.controller.js